import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState, useEffect, useRef } from "react";
import { Calendar, MapPin, ArrowLeft, Minus, Plus, ShoppingCart, Check, Gift } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { stripeEnvironment } from "@/lib/stripe";
import { getCheckoutFunctionName, logCheckoutProvider } from "@/lib/payment-provider";
import { extractCheckoutErrorMessage } from "@/lib/checkout-errors";
import { formatPrice, formatDate } from "@/lib/mock-data";
import { computeServiceFee, computeDisplayPrice, usePlatformFeeConfig } from "@/lib/fees";
import { claimFreeTickets } from "@/lib/free-checkout.functions";
import { VenueZoneSelector } from "@/components/event/VenueZoneSelector";
import { TenantThemeProvider, PoweredByZyntra } from "@/components/TenantThemeProvider";
import { getBrandingForEvent, type TenantBranding } from "@/lib/branding.functions";
import type { Tables } from "@/integrations/supabase/types";


type EventWithTicketTypes = Tables<"events"> & {
  ticket_types: Tables<"ticket_types">[];
};

export const Route = createFileRoute("/events/$eventId")({
  component: EventDetail,
  loader: async ({ params }) => {
    // Lectura cacheada (KV 120s + fallback memoria). Tolerable: SEO/OG
    // pueden tener hasta 2min de staleness; la purga se dispara en
    // updateEvent/cancelEvent/deleteEvent.
    const { getPublicEventDetail } = await import("@/lib/events-public.functions");
    try {
      const data = await getPublicEventDetail({ data: { eventId: params.eventId } });
      return { eventMeta: data };
    } catch {
      // Fallback a lectura directa si la server fn falla en SSR.
      const { data } = await supabase
        .from("events")
        .select("id, title, description, date, time, location, city, image_url, category, ticket_types(price, currency)")
        .eq("id", params.eventId)
        .eq("is_published", true)
        .is("deleted_at", null)
        .maybeSingle();
      return { eventMeta: data };
    }
  },
  head: ({ params, loaderData }) => {
    const ev = loaderData?.eventMeta;
    const url = `https://zyntratickets.com/events/${params.eventId}`;
    if (!ev) {
      return {
        meta: [
          { title: "Evento — Zyntra Tickets" },
          { property: "og:url", content: url },
        ],
        links: [{ rel: "canonical", href: url }],
      };
    }
    const rawDesc = (ev.description || `${ev.title} · ${ev.location}, ${ev.city}`).replace(/\s+/g, " ").trim();
    const description = rawDesc.length > 160 ? rawDesc.slice(0, 157) + "…" : rawDesc;
    const titleBase = `${ev.title} — Zyntra Tickets`;
    const title = titleBase.length > 60 ? `${ev.title.slice(0, 55)}…` : titleBase;
    const prices = (ev.ticket_types || []).map((t: any) => Number(t.price)).filter((n: number) => !isNaN(n));
    const lowPrice = prices.length ? Math.min(...prices) : undefined;
    const highPrice = prices.length ? Math.max(...prices) : undefined;
    const currency = (ev.ticket_types?.[0] as any)?.currency || "ARS";
    const startDate = ev.date && ev.time ? `${ev.date}T${ev.time}` : ev.date;
    const jsonLd: Record<string, unknown> = {
      "@context": "https://schema.org",
      "@type": "Event",
      name: ev.title,
      startDate,
      eventStatus: "https://schema.org/EventScheduled",
      eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
      location: {
        "@type": "Place",
        name: ev.location,
        address: { "@type": "PostalAddress", addressLocality: ev.city, addressCountry: "AR" },
      },
      description,
      url,
    };
    if (ev.image_url) jsonLd.image = ev.image_url;
    if (lowPrice !== undefined) {
      jsonLd.offers = {
        "@type": "AggregateOffer",
        priceCurrency: currency,
        lowPrice,
        highPrice,
        url,
        availability: "https://schema.org/InStock",
      };
    }
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: ev.title },
        { property: "og:description", content: description },
        { property: "og:type", content: "event" },
        { property: "og:url", content: url },
        ...(ev.image_url ? [{ property: "og:image", content: ev.image_url }, { name: "twitter:image", content: ev.image_url }] : []),
        { name: "twitter:title", content: ev.title },
        { name: "twitter:description", content: description },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(jsonLd) },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="flex min-h-[60vh] items-center justify-center">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-foreground">Evento no encontrado</h2>
        <Link to="/" className="mt-4 inline-block text-primary hover:underline">Volver al inicio</Link>
      </div>
    </div>
  ),
});

function EventDetail() {
  const { eventId } = Route.useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const feeCfg = usePlatformFeeConfig();
  const [event, setEvent] = useState<EventWithTicketTypes | null>(null);
  const [branding, setBranding] = useState<TenantBranding | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getBrandingForEvent(eventId)
      .then(setBranding)
      .catch((e) => console.error("[branding] load failed", e));
  }, [eventId]);
  const [selected, setSelected] = useState<Record<string, number>>({});
  const [purchased, setPurchased] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [error, setError] = useState("");
  // Stable idempotency key per checkout attempt — regenerated only when the
  // selection changes (so a real "new attempt" gets a new key, but accidental
  // double-clicks during one attempt reuse the same key and hit the cache).
  const idemKeyRef = useRef<string | null>(null);
  const selectionSignature = JSON.stringify(
    Object.entries(selected).sort(([a], [b]) => a.localeCompare(b))
  );
  useEffect(() => {
    idemKeyRef.current = null;
  }, [selectionSignature]);

  useEffect(() => {
    loadEvent();
  }, [eventId]);

  async function loadEvent() {
    setLoading(true);
    const { data } = await supabase
      .from("events")
      .select("*, ticket_types(*)")
      .eq("id", eventId)
      .single();

    setEvent(data as EventWithTicketTypes | null);
    setLoading(false);
  }

  if (loading) {
    return (
      <div className="mx-auto max-w-5xl px-4 pb-20 pt-10 sm:px-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 w-20 bg-muted rounded" />
          <div className="aspect-[16/9] bg-muted rounded-2xl" />
          <div className="h-8 w-2/3 bg-muted rounded" />
          <div className="h-4 w-1/2 bg-muted rounded" />
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-foreground">Evento no encontrado</h2>
          <Link to="/" className="mt-4 inline-block text-primary hover:underline">Volver al inicio</Link>
        </div>
      </div>
    );
  }

  const totalItems = Object.values(selected).reduce((a: number, b: number) => a + b, 0);
  const subtotal = Object.entries(selected).reduce((sum: number, [id, qty]: [string, number]) => {
    const tt = event.ticket_types.find((t) => t.id === id);
    return sum + (tt ? tt.price * qty : 0);
  }, 0);
  // Service fee NEVER applies to free items.
  const totalServiceFee = Object.entries(selected).reduce((sum: number, [id, qty]: [string, number]) => {
    const tt = event.ticket_types.find((t) => t.id === id);
    if (!tt || tt.price <= 0) return sum;
    return sum + computeServiceFee(tt.price, feeCfg) * qty;
  }, 0);
  const totalPrice = subtotal + totalServiceFee;

  // Classify cart: all-free, all-paid, or mixed. All-free skips the payment gateway.
  const cartHasPaid = Object.entries(selected).some(([id, qty]) => {
    if (qty <= 0) return false;
    const tt = event.ticket_types.find((t) => t.id === id);
    return !!tt && tt.price > 0;
  });
  const cartHasFree = Object.entries(selected).some(([id, qty]) => {
    if (qty <= 0) return false;
    const tt = event.ticket_types.find((t) => t.id === id);
    return !!tt && tt.price <= 0;
  });
  const cartIsFreeOnly = cartHasFree && !cartHasPaid;

  function updateQty(tt: Tables<"ticket_types">, delta: number) {
    setSelected((prev) => {
      const current = prev[tt.id] || 0;
      const next = Math.max(0, Math.min(current + delta, tt.available, event!.max_per_user));
      const copy = { ...prev };
      if (next === 0) delete copy[tt.id];
      else copy[tt.id] = next;
      return copy;
    });
  }

  async function handlePurchase() {
    if (!user) {
      navigate({
        to: "/login",
        search: { redirect: `${window.location.pathname}${window.location.search}` } as any,
      });
      return;
    }
    setError("");
    setPurchasing(true);
    try {
      const items = Object.entries(selected).map(([ticketTypeId, quantity]) => ({
        ticketTypeId,
        quantity,
      }));
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) throw new Error("Sesión expirada. Iniciá sesión nuevamente.");

      // Free-only cart — bypass any payment gateway.
      if (cartIsFreeOnly) {
        await claimFreeTickets({
          data: { eventId: event!.id, items },
          headers: { authorization: `Bearer ${token}` },
        } as any);
        setPurchased(true);
        setPurchasing(false);
        return;
      }

      if (!idemKeyRef.current) {
        idemKeyRef.current =
          (crypto as any).randomUUID?.() ??
          `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      }
      const idemKey = idemKeyRef.current!;
      const provider = logCheckoutProvider("ticket");
      const fnName = getCheckoutFunctionName("ticket", provider);
      const { data, error: fnError } = await supabase.functions.invoke(fnName, {
        body: {
          eventId: event!.id,
          items,
          environment: stripeEnvironment,
          returnUrl: `${window.location.origin}/checkout/return?session_id={CHECKOUT_SESSION_ID}`,
        },
        headers: {
          Authorization: `Bearer ${token}`,
          "Idempotency-Key": idemKey,
        },
      });

      if (fnError) {
        const msg = await extractCheckoutErrorMessage(fnError, "Error al iniciar el pago");
        throw new Error(msg);
      }
      // ONVO: redirect a hosted checkout
      if (provider === "onvopay") {
        if (!data?.url) throw new Error("OnvoPay no devolvió URL de checkout");
        window.location.href = data.url;
        return;
      }
      if (!data?.clientSecret || !data?.sessionId) throw new Error("No se pudo crear la sesión de pago");

      navigate({
        to: "/checkout/$sessionId",
        params: { sessionId: data.sessionId },
        search: { secret: data.clientSecret, eventId: event!.id },
      });
    } catch (err: any) {
      setError(err.message || "Error al comprar");
      setPurchasing(false);
    }
  }


  if (purchased) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center px-4">
        <div className="max-w-md text-center">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-success/20">
            <Check className="h-8 w-8 text-success" />
          </div>
          <h2 className="text-2xl font-bold text-foreground">¡Compra exitosa!</h2>
          <p className="mt-2 text-muted-foreground">
            Tus {totalItems} entrada(s) para <strong>{event.title}</strong> ya están disponibles.
          </p>
          <div className="mt-6 flex justify-center gap-3">
            <Link to="/my-tickets" className="rounded-xl gradient-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground">
              Ver mis entradas
            </Link>
            <Link to="/" className="rounded-xl border border-border bg-secondary px-5 py-2.5 text-sm font-semibold text-secondary-foreground">
              Explorar más
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (event.uses_venue_map) {
    return (
      <TenantThemeProvider branding={branding}>
      <div className="mx-auto max-w-7xl px-4 pb-20 pt-6 sm:px-6">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors">
          <ArrowLeft className="h-4 w-4" />
          Volver
        </Link>

        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-1">
            <div className="overflow-hidden rounded-2xl">
              <img src={event.image_url || ""} alt={event.title} className="aspect-[16/9] w-full object-cover" />
            </div>
            <div className="mt-4">
              <span className="rounded-full bg-accent px-3 py-1 text-xs font-medium text-accent-foreground">
                {event.category}
              </span>
              <h1 className="mt-3 text-2xl font-black text-foreground sm:text-3xl">{event.title}</h1>
              <div className="mt-3 flex flex-col gap-2">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Calendar className="h-4 w-4 text-primary" />
                  {formatDate(event.date)} · {event.time}
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <MapPin className="h-4 w-4 text-primary" />
                  {event.location}, {event.city}
                </div>
              </div>
              {event.description && (
                <p className="mt-4 text-sm text-muted-foreground leading-relaxed">{event.description}</p>
              )}
            </div>
          </div>

          <div className="lg:col-span-2">
            <VenueZoneSelector eventId={event.id} eventTitle={event.title} />
          </div>
        </div>
      </div>
      </TenantThemeProvider>
    );
  }

  return (
    <TenantThemeProvider branding={branding}>
    <div className="mx-auto max-w-5xl px-4 pb-20 pt-6 sm:px-6">
      <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors">
        <ArrowLeft className="h-4 w-4" />
        Volver
      </Link>

      <div className="grid gap-8 lg:grid-cols-5">
        <div className="lg:col-span-3">
          <div className="overflow-hidden rounded-2xl">
            <img src={event.image_url || ""} alt={event.title} className="aspect-[16/9] w-full object-cover" />
          </div>
          <div className="mt-6">
            <span className="rounded-full bg-accent px-3 py-1 text-xs font-medium text-accent-foreground">
              {event.category}
            </span>
            <h1 className="mt-3 text-3xl font-black text-foreground sm:text-4xl">{event.title}</h1>
            <div className="mt-4 flex flex-col gap-2">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Calendar className="h-4 w-4 text-primary" />
                {formatDate(event.date)} · {event.time}
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <MapPin className="h-4 w-4 text-primary" />
                {event.location}, {event.city}
              </div>
            </div>
            <p className="mt-6 text-sm text-muted-foreground leading-relaxed">{event.description}</p>
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="sticky top-24">
            <div className="rounded-2xl border border-border bg-card p-5">
              <h2 className="text-lg font-bold text-card-foreground mb-4">Seleccioná tus entradas</h2>
              <p className="text-xs text-muted-foreground mb-1">Máximo {event.max_per_user} por persona</p>
              <p className="text-[11px] text-muted-foreground/80 mb-4">Los precios incluyen cargos por servicio.</p>

              <div className="space-y-3">
                {event.ticket_types.map((tt) => {
                  const qty = selected[tt.id] || 0;
                  const soldOut = tt.available === 0;
                  const displayPrice = computeDisplayPrice(tt.price, feeCfg);

                  return (
                    <div
                      key={tt.id}
                      className={`rounded-xl border p-4 transition-colors ${
                        qty > 0 ? "border-primary bg-accent/50" : "border-border"
                      } ${soldOut ? "opacity-50" : ""}`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="text-sm font-bold text-card-foreground">{tt.name}</h3>
                          <p className="text-xs text-muted-foreground mt-0.5">{tt.description}</p>
                          {tt.price <= 0 ? (
                            <p className="mt-1 inline-flex items-center gap-1 rounded-full bg-success/15 px-2 py-0.5 text-xs font-bold text-success">
                              <Gift className="h-3 w-3" /> Gratis
                            </p>
                          ) : (
                            <p className="text-sm font-bold text-primary mt-1">{formatPrice(displayPrice)}</p>
                          )}

                        </div>
                        {!soldOut ? (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => updateQty(tt, -1)}
                              disabled={qty === 0}
                              aria-label="Disminuir cantidad"
                              className="flex h-7 w-7 items-center justify-center rounded-lg border border-border bg-secondary text-secondary-foreground disabled:opacity-30"
                            >
                              <Minus className="h-3 w-3" />
                            </button>
                            <span className="w-5 text-center text-sm font-bold text-card-foreground">{qty}</span>
                            <button
                              onClick={() => updateQty(tt, 1)}
                              disabled={qty >= Math.min(tt.available, event.max_per_user)}
                              aria-label="Aumentar cantidad"
                              className="flex h-7 w-7 items-center justify-center rounded-lg gradient-primary text-primary-foreground disabled:opacity-30"
                            >
                              <Plus className="h-3 w-3" />
                            </button>
                          </div>
                        ) : (
                          <span className="text-xs font-medium text-destructive">Agotado</span>
                        )}
                      </div>
                      {!soldOut && (
                        <p className="text-xs text-muted-foreground mt-2">{tt.available} disponibles</p>
                      )}
                    </div>
                  );
                })}
              </div>

              {error && (
                <p className="mt-3 rounded-lg bg-destructive/10 px-3 py-2 text-xs text-destructive">{error}</p>
              )}

              {totalItems > 0 && (
                <div className="mt-5 rounded-xl border border-border bg-surface p-4">
                  <div className="space-y-1.5 text-sm">
                    <div className="flex items-center justify-between text-muted-foreground">
                      <span>Subtotal ({totalItems} entrada{totalItems > 1 ? "s" : ""})</span>
                      <span>{formatPrice(subtotal)}</span>
                    </div>
                    {totalServiceFee > 0 && (
                      <div className="flex items-center justify-between text-muted-foreground">
                        <span>Cargo por servicio</span>
                        <span>{formatPrice(totalServiceFee)}</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between border-t border-border pt-1.5">
                      <span className="font-semibold text-foreground">Total</span>
                      <span className="text-lg font-black text-foreground">
                        {cartIsFreeOnly ? "Gratis" : formatPrice(totalPrice)}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={handlePurchase}
                    disabled={purchasing}
                    className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl gradient-primary py-3 text-sm font-bold text-primary-foreground transition-all hover:opacity-90 glow-primary disabled:opacity-50"
                  >
                    {cartIsFreeOnly ? <Gift className="h-4 w-4" /> : <ShoppingCart className="h-4 w-4" />}
                    {purchasing
                      ? "Procesando..."
                      : !user
                        ? "Ingresar para reservar"
                        : cartIsFreeOnly
                          ? "Reservar gratis"
                          : "Comprar ahora"}
                  </button>

                </div>
              )}
              <PoweredByZyntra className="mt-3 text-center" />
            </div>
          </div>
        </div>
      </div>
    </div>
    </TenantThemeProvider>
  );
}
